// src/pages/map/mapView.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "./mapView.css";

// =======================
// Assets (fallback)
// =======================
import company001 from "../../assets/company/001.png"; // fallback genérico

// fallback para usuário (se não achar o picture)
import defaultProfile from "../../assets/profile/profile-5.jpg";

// ícone genérico de veículo
import vehicle001 from "../../assets/vehicles/002.png";

// ícone de antena AIS (placeholder)
import antenna001 from "../../assets/company/001.png";

// =======================
// Tipagens
// =======================
type EmpresaLocalizacao = {
  id: string;
  empresa_id: string;
  apelido: string;
  endereco: string;
  latitude: string;
  longitude: string;
  raio_metros: number;
  principal: boolean;
  picture?: string | null; // <- VEM DA API, ex: "001"
};

type UsuarioLocalizacao = {
  id: number | string;
  empresa_id: string;
  nome: string;
  picture: string | null;
  latitude: string;
  longitude: string;
  // campos de timestamp vindos da API
  ultima_geo_atualizacao?: string | null;
  last_update?: string | null;
  ultima_atualizacao?: string | null;
  updated_at?: string | null;
};

type VeiculoLocalizacao = {
  id: string | number;
  empresa_id: string | number;
  apelido: string;
  placa: string;
  descricao?: string | null;
  latitude: string;
  longitude: string;
  ultima_atualizacao?: string | null;
};

// Para armazenar posição anterior + heading de cada veículo
type VehicleTrackState = {
  lat: number;
  lon: number;
  heading: number; // em graus (0..360)
};

type AntenaLocalizacao = {
  id: string;
  nome: string;
  latitude: number;
  longitude: number;
  raio_metros: number;
};

// =======================
// Resolver dinâmico de profiles
// =======================
const profileCtx: any = (require as any).context(
  "../../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

function resolveProfilePic(picture?: string | null): string {
  if (!picture) return defaultProfile;

  const candidates = [
    `./${picture}.jpg`,
    `./${picture}.jpeg`,
    `./${picture}.png`,
  ];

  for (const c of candidates) {
    try {
      return profileCtx(c);
    } catch {
      // tenta o próximo
    }
  }

  return defaultProfile;
}

// =======================
// Resolver dinâmico de logos de empresa
// =======================
// Vai procurar exatamente na pasta assets/company o arquivo:
// ./<picture>.png / .jpg / .jpeg
const companyCtx: any = (require as any).context(
  "../../assets/company",
  false,
  /\.(jpe?g|png)$/i
);

function resolveCompanyPic(picture?: string | null): string {
  if (picture) {
    const candidates = [
      `./${picture}.png`,
      `./${picture}.jpg`,
      `./${picture}.jpeg`,
    ];

    for (const c of candidates) {
      try {
        return companyCtx(c);
      } catch {
        // tenta o próximo
      }
    }
  }

  // fallback genérico caso picture não exista ou não seja encontrado
  return company001;
}

// =======================
// Mapbox config
// =======================
const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZ3VpbGhlcm1lY2xocyIsImEiOiJjbWk3cXlwMWswNDRnMmxwdmtsaHZmcDgxIn0.sD-dsYdwOSUKzUySugqvRQ";

const MAPBOX_STYLE_ID = "mapbox/streets-v11";

const MAPBOX_TILES_URL = `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_ID}/tiles/256/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`;

const MAP_DEFAULT_CENTER: [number, number] = [-22.89, -43.115];
const MAP_DEFAULT_ZOOM = 14;

const API_URL =
  "https://api.eva-googlemarine.com/geral/get-empresas-localizacao";

// limiar em segundos para considerar “recente”
const USER_FRESH_THRESHOLD_SEC = 90;
const VEHICLE_FRESH_THRESHOLD_SEC = 180; // pode ser outro valor se quiser

// =======================
// Antenas fixas (AIS / RF)
// =======================
const ANTENAS: AntenaLocalizacao[] = [
  {
    id: "ant-1",
    nome: "Antena AIS 01",
    latitude: -22.879446975207657,
    longitude: -43.13298917406165,
    raio_metros: 20000,
  },
];

// =======================
// Ícones
// =======================

// Ícone de empresa usando SOMENTE o picture vindo da API
function getCompanyIcon(item: EmpresaLocalizacao): L.Icon {
  const iconUrl = resolveCompanyPic(item.picture ?? null);
  const size: [number, number] = item.principal ? [42, 42] : [34, 34];

  return L.icon({
    iconUrl,
    iconRetinaUrl: iconUrl,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1]],
    popupAnchor: [0, -size[1] + 6],
  });
}

// Ícone de usuário como círculo com borda (via divIcon + CSS)
function createUserIcon(picture: string | null, isFresh: boolean): L.DivIcon {
  const url = resolveProfilePic(picture);
  const borderColor = isFresh ? "#22c55e" : "#ef4444"; // verde / vermelho

  return L.divIcon({
    html: `
      <div class="mapview-user-marker" style="border: 3px solid ${borderColor};">
        <img src="${url}" alt="user" />
      </div>
    `,
    className: "",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -26],
  });
}

// ==== helper para calcular bearing (direção) entre 2 pontos ====
function computeBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);
  let brng = (toDeg(θ) + 360) % 360;

  return brng;
}

// Ícone de veículo com rotação conforme heading
function getVehicleIcon(angleDeg: number): L.DivIcon {
  const size: [number, number] = [30, 30]; // menor pra não ocupar tanta rua

  return L.divIcon({
    html: `
      <div
        style="
          width:${size[0]}px;
          height:${size[1]}px;
          display:flex;
          align-items:center;
          justify-content:center;
          transform: translate3d(0,0,0) rotate(${angleDeg}deg);
          transform-origin: center center;
        "
      >
        <img
          src="${vehicle001}"
          alt="veículo"
          style="
            width:100%;
            height:100%;
            object-fit:contain;
            pointer-events:none;
          "
        />
      </div>
    `,
    className: "",
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1] / 2],
    popupAnchor: [0, -size[1] / 2],
  });
}

// Ícone de antena
function getAntennaIcon(): L.Icon {
  const size: [number, number] = [44, 44];

  return L.icon({
    iconUrl: antenna001,
    iconRetinaUrl: antenna001,
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1]],
    popupAnchor: [0, -size[1] + 6],
  });
}

// =======================
// Helpers de distância
// =======================

function isValidLatLon(latStr: string, lonStr: string) {
  const lat = parseFloat(latStr);
  const lon = parseFloat(lonStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return true;
}

function distanceMeters(
  lat1Str: string,
  lon1Str: string,
  lat2Str: string,
  lon2Str: string
): number | null {
  const lat1 = parseFloat(lat1Str);
  const lon1 = parseFloat(lon1Str);
  const lat2 = parseFloat(lat2Str);
  const lon2 = parseFloat(lon2Str);

  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  ) {
    return null;
  }

  const p1 = L.latLng(lat1, lon1);
  const p2 = L.latLng(lat2, lon2);
  return p1.distanceTo(p2); // em metros
}

// =======================
// Helpers de tempo da última atualização
// =======================

function getUserTimestampString(user: UsuarioLocalizacao): string | null {
  return (
    user.ultima_geo_atualizacao ??
    user.last_update ??
    user.ultima_atualizacao ??
    user.updated_at ??
    null
  );
}

function getUserLastUpdateAgeSeconds(
  user: UsuarioLocalizacao,
  nowMs: number
): number | null {
  const tsStr = getUserTimestampString(user);
  if (!tsStr) return null;

  const tsMs = Date.parse(tsStr);
  if (Number.isNaN(tsMs)) return null;

  const diffMs = nowMs - tsMs;
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;

  return diffMs / 1000;
}

function getVehicleLastUpdateAgeSeconds(
  veh: VeiculoLocalizacao,
  nowMs: number
): number | null {
  const tsStr = veh.ultima_atualizacao;
  if (!tsStr) return null;

  const tsMs = Date.parse(tsStr);
  if (Number.isNaN(tsMs)) return null;

  const diffMs = nowMs - tsMs;
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;

  return diffMs / 1000;
}

function formatAgeSeconds(ageSec: number | null): string {
  if (ageSec === null) return "desconhecido";

  if (ageSec < 60) {
    const s = Math.round(ageSec);
    return `${s} s`;
  }

  if (ageSec < 3600) {
    const m = Math.round(ageSec / 60);
    return `${m} min`;
  }

  const h = Math.round(ageSec / 3600);
  return `${h} h`;
}

// =======================
// Componente principal
// =======================
const MapView: React.FC = () => {
  const [empresas, setEmpresas] = useState<EmpresaLocalizacao[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioLocalizacao[]>([]);
  const [veiculos, setVeiculos] = useState<VeiculoLocalizacao[]>([]);
  const [error, setError] = useState<string | null>(null);

  // “relógio” para poder recalcular o tempo desde a última atualização
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  // Estado com heading (graus) de cada veículo
  const [vehicleHeadings, setVehicleHeadings] = useState<
    Record<string, number>
  >({});

  // Estado para controlar se os tiles iniciais já carregaram
  const [tilesLoaded, setTilesLoaded] = useState(false);

  // Ref para guardar posição anterior de cada veículo entre fetches
  const prevVehiclesRef = useRef<Map<string, VehicleTrackState>>(new Map());

  // Atualiza relógio local a cada 10s
  useEffect(() => {
    const id = window.setInterval(() => {
      setNowMs(Date.now());
    }, 10000);

    return () => {
      window.clearInterval(id);
    };
  }, []);

  // Busca empresas/usuarios/veiculos a cada 10s
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        if (!mounted) return;

        setError(null);

        const res = await fetch(API_URL, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!mounted) return;

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(
            `Falha ao buscar localizações (HTTP ${res.status}): ${text}`
          );
        }

        const json = await res.json();
        if (!json?.ok) {
          throw new Error("Resposta inesperada da API");
        }

        const empresasData = Array.isArray(json.empresas)
          ? (json.empresas as EmpresaLocalizacao[])
          : Array.isArray(json.data)
          ? (json.data as EmpresaLocalizacao[])
          : [];

        const usuariosData = Array.isArray(json.usuarios)
          ? (json.usuarios as UsuarioLocalizacao[])
          : [];

        const veiculosData = Array.isArray(json.veiculos)
          ? (json.veiculos as VeiculoLocalizacao[])
          : [];

        // Atualizar heading dos veículos com base na posição anterior
        const newTrackMap = new Map<string, VehicleTrackState>();
        const newHeadingObj: Record<string, number> = {};

        for (const v of veiculosData) {
          const key = String(v.id);
          const lat = parseFloat(v.latitude);
          const lon = parseFloat(v.longitude);

          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            continue;
          }

          const prev = prevVehiclesRef.current.get(key);
          let heading = prev?.heading ?? 0;

          if (prev) {
            const dist = distanceMeters(
              String(prev.lat),
              String(prev.lon),
              String(lat),
              String(lon)
            );
            if (dist !== null && dist > 2) {
              heading = computeBearing(prev.lat, prev.lon, lat, lon);
            }
          }

          newTrackMap.set(key, { lat, lon, heading });
          newHeadingObj[key] = heading;
        }

        prevVehiclesRef.current = newTrackMap;
        setVehicleHeadings(newHeadingObj);

        setEmpresas(empresasData);
        setUsuarios(usuariosData);
        setVeiculos(veiculosData);
      } catch (err: any) {
        if (!mounted) return;
        console.error(
          "Erro ao buscar empresas/usuarios/veiculos localização:",
          err
        );
        setError(String(err?.message || err));
      }
    };

    void fetchData();

    const intervalId = window.setInterval(() => {
      void fetchData();
    }, 10000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  // Centro do mapa: média de todas as coordenadas
  const mapCenter = useMemo<[number, number]>(() => {
    const coords: { lat: number; lon: number }[] = [];

    empresas.forEach((e) => {
      const lat = parseFloat(e.latitude);
      const lon = parseFloat(e.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        coords.push({ lat, lon });
      }
    });

    usuarios.forEach((u) => {
      const lat = parseFloat(u.latitude);
      const lon = parseFloat(u.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        coords.push({ lat, lon });
      }
    });

    veiculos.forEach((v) => {
      const lat = parseFloat(v.latitude);
      const lon = parseFloat(v.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        coords.push({ lat, lon });
      }
    });

    ANTENAS.forEach((a) => {
      if (Number.isFinite(a.latitude) && Number.isFinite(a.longitude)) {
        coords.push({ lat: a.latitude, lon: a.longitude });
      }
    });

    if (!coords.length) return MAP_DEFAULT_CENTER;

    const avgLat = coords.reduce((acc, c) => acc + c.lat, 0) / coords.length;
    const avgLon = coords.reduce((acc, c) => acc + c.lon, 0) / coords.length;

    return [avgLat, avgLon];
  }, [empresas, usuarios, veiculos]);

  // Usuário dentro do raio de QUALQUER empresa
  function isUserInsideAnyEmpresaRadius(user: UsuarioLocalizacao): boolean {
    if (!isValidLatLon(user.latitude, user.longitude)) return false;

    return empresas.some((emp) => {
      const raio = Number(emp.raio_metros || 0);
      if (!Number.isFinite(raio) || raio <= 0) return false;

      const dist = distanceMeters(
        user.latitude,
        user.longitude,
        emp.latitude,
        emp.longitude
      );

      if (dist === null) return false;
      return dist <= raio;
    });
  }

  return (
    <div className="mapview-root">
      {/* Overlay de carregamento do mapa */}
      {!tilesLoaded && (
        <div className="mapview-loading-overlay">
          <div className="mapview-loading-box">
            <span>Carregando mapa...</span>
          </div>
        </div>
      )}

      <MapContainer
        center={mapCenter}
        zoom={MAP_DEFAULT_ZOOM}
        className="mapview-map"
        scrollWheelZoom={true}
        zoomControl={true}
      >
        {/* TILE DO MAPBOX */}
        <TileLayer
          url={MAPBOX_TILES_URL}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · Imagery &copy; <a href="https://www.mapbox.com/">Mapbox</a>'
          // Deixa o mapa invisível até os tiles carregarem
          opacity={tilesLoaded ? 1 : 0}
          // Pré-carrega tiles em volta da viewport
          keepBuffer={3}
          updateWhenIdle={false}
          updateWhenZooming={true}
          eventHandlers={{
            load: () => {
              // "load" dispara quando todos os tiles visíveis foram carregados pelo menos uma vez
              setTilesLoaded(true);
            },
          }}
        />

        {/* MARCADORES DE EMPRESAS */}
        {empresas.map((item) => {
          const lat = parseFloat(item.latitude);
          const lon = parseFloat(item.longitude);
          const raio = Number(item.raio_metros || 0);

          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return null;
          }

          const usuariosDentro = usuarios.filter((u) => {
            if (!isValidLatLon(u.latitude, u.longitude)) return false;
            if (!Number.isFinite(raio) || raio <= 0) return false;

            const dist = distanceMeters(
              u.latitude,
              u.longitude,
              item.latitude,
              item.longitude
            );
            if (dist === null) return false;

            return dist <= raio;
          });

          const usuariosAutorizados = usuariosDentro.filter(
            (u) => String(u.empresa_id) === String(item.empresa_id)
          );
          const usuariosNaoAutorizados = usuariosDentro.filter(
            (u) => String(u.empresa_id) !== String(item.empresa_id)
          );

          return (
            <React.Fragment key={`empresa-${item.id}`}>
              <Marker position={[lat, lon]} icon={getCompanyIcon(item)}>
                <Popup>
                  <div
                    style={{
                      fontFamily:
                        "Poppins, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      fontSize: 14,
                      maxWidth: 260,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      {item.apelido || "Empresa"}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        marginBottom: 4,
                        color: "#374151",
                      }}
                    >
                      {item.endereco}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 8,
                      }}
                    >
                      empresa_id: {item.empresa_id}
                      <br />
                      raio: {raio} m{item.principal ? " (principal)" : ""}
                      {item.picture && (
                        <>
                          <br />
                          picture: {item.picture}
                        </>
                      )}
                    </div>

                    {/* Pessoas da mesma empresa dentro do raio */}
                    <div
                      style={{
                        marginTop: 4,
                        borderTop: "1px solid #e5e7eb",
                        paddingTop: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 2,
                        }}
                      >
                        Pessoas desta empresa no raio (
                        {usuariosAutorizados.length})
                      </div>
                      {usuariosAutorizados.length === 0 ? (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                          }}
                        >
                          Nenhuma pessoa desta empresa dentro do raio.
                        </div>
                      ) : (
                        <ul
                          style={{
                            listStyle: "none",
                            paddingLeft: 0,
                            margin: 0,
                          }}
                        >
                          {usuariosAutorizados.map((u) => {
                            const ageSec = getUserLastUpdateAgeSeconds(
                              u,
                              nowMs
                            );
                            const isFresh =
                              ageSec !== null &&
                              ageSec <= USER_FRESH_THRESHOLD_SEC;
                            const avatarUrl = resolveProfilePic(u.picture);

                            return (
                              <li
                                key={`emp-${item.id}-auth-${u.id}`}
                                style={{
                                  fontSize: 11,
                                  color: "#111827",
                                  marginBottom: 4,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <img
                                    src={avatarUrl}
                                    alt={u.nome}
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "999px",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <div>
                                    {u.nome}{" "}
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: isFresh ? "#16a34a" : "#b91c1c",
                                      }}
                                    >
                                      (última atualização:{" "}
                                      {formatAgeSeconds(ageSec)})
                                    </span>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    {/* Pessoas de outras empresas dentro do raio */}
                    <div
                      style={{
                        marginTop: 6,
                        borderTop: "1px solid #e5e7eb",
                        paddingTop: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          marginBottom: 2,
                          color: "#b91c1c",
                        }}
                      >
                        Pessoas NÃO autorizadas no raio (
                        {usuariosNaoAutorizados.length})
                      </div>
                      {usuariosNaoAutorizados.length === 0 ? (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#9ca3af",
                          }}
                        >
                          Nenhuma pessoa de outras empresas dentro do raio.
                        </div>
                      ) : (
                        <ul
                          style={{
                            listStyle: "none",
                            paddingLeft: 0,
                            margin: 0,
                          }}
                        >
                          {usuariosNaoAutorizados.map((u) => {
                            const ageSec = getUserLastUpdateAgeSeconds(
                              u,
                              nowMs
                            );
                            const isFresh =
                              ageSec !== null &&
                              ageSec <= USER_FRESH_THRESHOLD_SEC;
                            const avatarUrl = resolveProfilePic(u.picture);

                            return (
                              <li
                                key={`emp-${item.id}-na-${u.id}`}
                                style={{
                                  fontSize: 11,
                                  color: "#7f1d1d",
                                  marginBottom: 4,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <img
                                    src={avatarUrl}
                                    alt={u.nome}
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "999px",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <div>
                                    {u.nome} (empresa_id: {u.empresa_id}){" "}
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: isFresh ? "#16a34a" : "#b91c1c",
                                      }}
                                    >
                                      (última atualização:{" "}
                                      {formatAgeSeconds(ageSec)})
                                    </span>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>

              {Number.isFinite(raio) && raio > 0 && (
                <Circle
                  center={[lat, lon]}
                  radius={raio}
                  pathOptions={{
                    color: item.principal ? "#f97316" : "#2563eb",
                    weight: 1,
                    fillOpacity: 0.15,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* MARCADORES DE USUÁRIOS (apenas fora do raio de QUALQUER empresa) */}
        {usuarios.map((u) => {
          const lat = parseFloat(u.latitude);
          const lon = parseFloat(u.longitude);

          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return null;
          }

          const insideAny = isUserInsideAnyEmpresaRadius(u);
          if (insideAny) return null;

          const ageSec = getUserLastUpdateAgeSeconds(u, nowMs);
          const isFresh = ageSec !== null && ageSec <= USER_FRESH_THRESHOLD_SEC;

          return (
            <Marker
              key={`usuario-${u.id}`}
              position={[lat, lon]}
              icon={createUserIcon(u.picture, isFresh)}
            >
              <Popup>
                <div
                  style={{
                    fontFamily:
                      "Poppins, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: 14,
                    maxWidth: 260,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {u.nome}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginBottom: 4,
                    }}
                  >
                    empresa_id: {u.empresa_id}
                    <br />
                    lat: {u.latitude}
                    <br />
                    lon: {u.longitude}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: isFresh ? "#16a34a" : "#b91c1c",
                    }}
                  >
                    Última atualização: {formatAgeSeconds(ageSec)}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* MARCADORES DE VEÍCULOS */}
        {veiculos.map((v) => {
          const lat = parseFloat(v.latitude);
          const lon = parseFloat(v.longitude);

          if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return null;
          }

          const ageSec = getVehicleLastUpdateAgeSeconds(v, nowMs);
          const isFresh =
            ageSec !== null && ageSec <= VEHICLE_FRESH_THRESHOLD_SEC;

          const heading = vehicleHeadings[String(v.id)] ?? 0;

          return (
            <Marker
              key={`veiculo-${v.id}`}
              position={[lat, lon]}
              icon={getVehicleIcon(heading)}
            >
              <Popup>
                <div
                  style={{
                    fontFamily:
                      "Poppins, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: 14,
                    maxWidth: 260,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {v.apelido || "Veículo"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginBottom: 4,
                    }}
                  >
                    Placa: {v.placa}
                    <br />
                    empresa_id: {v.empresa_id}
                    <br />
                    lat: {v.latitude}
                    <br />
                    lon: {v.longitude}
                    <br />
                    direção aprox.: {Math.round(heading)}°
                  </div>
                  {v.descricao && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#4b5563",
                        marginBottom: 4,
                      }}
                    >
                      {v.descricao}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 12,
                      color: isFresh ? "#16a34a" : "#b91c1c",
                    }}
                  >
                    Última atualização: {formatAgeSeconds(ageSec)}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* MARCADORES DE ANTENAS + ÁREA DE COBERTURA */}
        {ANTENAS.map((a) => (
          <React.Fragment key={a.id}>
            <Marker
              position={[a.latitude, a.longitude]}
              icon={getAntennaIcon()}
            >
              <Popup>
                <div
                  style={{
                    fontFamily:
                      "Poppins, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    fontSize: 14,
                    maxWidth: 260,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    {a.nome}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                    }}
                  >
                    lat: {a.latitude.toFixed(6)}
                    <br />
                    lon: {a.longitude.toFixed(6)}
                    <br />
                    raio de cobertura: {a.raio_metros} m
                  </div>
                </div>
              </Popup>
            </Marker>

            <Circle
              center={[a.latitude, a.longitude]}
              radius={a.raio_metros}
              pathOptions={{
                color: "#22C55E4F",
                weight: 1,
                fillColor: "#BBE5F7FF",
                fillOpacity: 0.05,
              }}
            />
          </React.Fragment>
        ))}
      </MapContainer>

      {error && (
        <div
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            backgroundColor: "#b91c1c",
            color: "#fee2e2",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontFamily:
              "Poppins, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            maxWidth: 260,
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.35)",
          }}
        >
          <strong>Erro ao carregar mapa</strong>
          <div style={{ marginTop: 4, fontSize: 11 }}>{error}</div>
        </div>
      )}
    </div>
  );
};

export default MapView;
